import React, { useEffect, useRef, useState } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { getUserChats } from '../utils/chatApi';
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";

const WS_URL = "https://dersmaster.com/ws";

async function markAllMessagesAsRead(chatRoomId) {
  const tokenObj = JSON.parse(localStorage.getItem('auth-storage'));
  const token = tokenObj?.state?.accessToken;
  await fetch(`/v1/chat/readAll/${chatRoomId}`, {
    method: "PUT",
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
}

const ChatDropdown = () => {
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const username = user?.username;
  const userId = user?.id;

  const unreadCount = chats.reduce((acc, c) => acc + (c.unreadCount || 0), 0);

  // İlk yüklemede ve username değiştiğinde chat'leri çek
  useEffect(() => {
    if (username) {
      getUserChats(username).then(setChats);
    }
  }, [username]);

  // Dropdown açıldığında chat'leri yenile
  useEffect(() => {
    if (open && username) {
      getUserChats(username).then(setChats);
    }
  }, [open, username]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!userId) return;

    const tokenObj = JSON.parse(localStorage.getItem("auth-storage") || "{}");
    const token = tokenObj?.state?.accessToken;
    const subscribeAddress = `/topic/notifications/${userId}`;

    const client = new Client({
      webSocketFactory: () => new SockJS(WS_URL),
      connectHeaders: token ? { Authorization: "Bearer " + token } : {},
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(subscribeAddress, (message) => {
          try {
            const notif = JSON.parse(message.body);
            if (notif.type === "CHAT") {
              // WebSocket bildirimi geldiğinde chat'leri yenile
              if (username) {
                getUserChats(username).then(setChats);
              }
            }
          } catch {}
        });
      },
      onStompError: () => {}
    });

    client.activate();

    return () => {
      client.deactivate();
    };
  }, [userId, username]);

  const latestChats = chats.slice(0, 5);

  const handleChatClick = async (chat) => {
    setChats(oldChats =>
      oldChats.map(c =>
        c.chatRoomId === chat.chatRoomId ? { ...c, unreadCount: 0 } : c
      )
    );
    await markAllMessagesAsRead(chat.chatRoomId);
    setOpen(false);
    navigate("/chat", { state: { openChatRoomId: chat.chatRoomId } });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="p-2 rounded-lg text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:text-gray-400 dark:hover:text-sky-300 dark:hover:bg-gray-800 transition-colors relative"
        aria-label="Mesajlar"
        onClick={() => setOpen(!open)}
      >
        <ChatBubbleLeftRightIcon className="h-6 w-6" />
        {unreadCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 rounded-full px-2 py-0.5 font-bold text-white shadow ring-2 ring-white"
            style={{
              background: "linear-gradient(90deg, #fca5a5 80%, #fb7185 100%)",
              fontSize: "12px",
              boxShadow: "0 2px 8px 0 #fb718511"
            }}
          >
            {unreadCount}
          </span>
        )}
      </button>
      <div
        className={`absolute right-0 mt-2 w-80 max-w-xs bg-white dark:bg-gray-800 rounded-xl shadow-lg py-2 transition-all duration-200 origin-top-right z-50 ${
          open ? 'opacity-100 visible scale-100 translate-y-0' : 'opacity-0 invisible scale-95 -translate-y-2'
        }`}
      >
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 font-medium text-gray-800 dark:text-white">
          Mesajlarım
        </div>
        <div className="max-h-80 overflow-y-auto">
          {latestChats.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Hiç mesaj yok.
            </div>
          ) : (
            latestChats.map(chat => {
              const isUnread = chat.unreadCount > 0;
              return (
                <button
                  key={chat.chatRoomId}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 transition-all text-left rounded-lg relative group
                    ${isUnread
                      ? "font-bold bg-gradient-to-r from-sky-100/80 via-white/80 to-white/60 dark:from-sky-900/70 dark:via-sky-950/80 dark:to-transparent text-sky-900 dark:text-sky-100 ring-1 ring-sky-200 dark:ring-sky-800 shadow-sm"
                      : "font-normal bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 opacity-90"
                    }
                  `}
                  style={isUnread ? { boxShadow: "0 1px 8px 0 #38bdf810" } : {}}
                  onClick={() => handleChatClick(chat)}
                >
                  {isUnread && (
                    <span
                      style={{
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: "7px",
                        background: "linear-gradient(180deg, #38bdf8 60%, #0ea5e9 100%)",
                        borderRadius: "7px 0 0 7px",
                        position: "absolute",
                        boxShadow: "0 2px 8px 0 #38bdf822"
                      }}
                    />
                  )}
                  {chat.profileImageUrl ? (
                    <img src={chat.profileImageUrl} alt={chat.displayName} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center shadow-inner">
                      <span className="text-lg font-bold text-sky-600 dark:text-sky-300">
                        {chat.displayName?.[0]?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 pl-1">
                    <div
                      className={`truncate ${
                        isUnread
                          ? "font-bold text-sky-900 dark:text-sky-100"
                          : "font-normal text-gray-700 dark:text-gray-200"
                      }`}
                      style={isUnread ? { fontSize: 17 } : { fontSize: 15 }}
                    >
                      {chat.displayName}
                    </div>
                    <div
                      className={`
                        truncate
                        ${isUnread
                          ? "font-semibold text-sky-700 dark:text-sky-400"
                          : "text-gray-400 dark:text-gray-500"
                        }
                      `}
                      style={isUnread ? { fontSize: 15 } : { fontSize: 13 }}
                    >
                      {chat.lastMessage}
                    </div>
                  </div>
                  {isUnread && (
                    <span
                      className="rounded-full px-2 py-0.5 font-bold ml-1 shadow"
                      style={{
                        background: "linear-gradient(90deg, #fca5a5 80%, #fb7185 100%)",
                        color: "white",
                        fontSize: "12px",
                        boxShadow: "0 2px 8px 0 #fb718511"
                      }}
                    >
                      {chat.unreadCount}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
        {chats.length > 5 && (
          <div className="py-2 px-4 border-t border-gray-100 dark:border-gray-700">
            <button
              className="text-sm text-sky-600 dark:text-sky-300 hover:underline"
              onClick={() => {
                setOpen(false);
                navigate('/chat');
              }}
            >
              Tüm mesajları gör
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatDropdown;
